import { useEffect, useId, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { account } from '../../services/appwrite/client';
import { reportClientError } from '../../services/appwrite/report-client-error';
import { countries } from '../../shared/data/countries';
import { availableTimeZones, timeZoneOffsetLabel } from './timezones';
import { createOwnProfile, type Disability, type RegisteredVia, type RegistrationInput } from './registration-gateway';
import { useSession } from './SessionContext';

/**
 * Appwrite vincula sola una cuenta de Google al mismo email verificado por
 * contraseña: no hay que distinguir "cómo se registró" en ese caso, alcanza
 * con leer el proveedor de la sesión activa en este momento.
 */
async function registeredVia(): Promise<RegisteredVia> {
  try {
    const session = await account.getSession({ sessionId: 'current' });
    return session.provider === 'google' ? 'google' : 'password';
  } catch {
    return 'password';
  }
}

interface FormValues {
  firstName: string;
  lastName: string;
  displayName: string;
  birthDate: string;
  country: string;
  timezone: string;
  gender: RegistrationInput['gender'] | '';
  pronoun: RegistrationInput['pronoun'] | '';
  accessibilityNotes: string;
  modality: RegistrationInput['modality'] | '';
}

const GENDER_OPTIONS: Array<{ value: RegistrationInput['gender']; label: string }> = [
  { value: 'female', label: 'Femenino' },
  { value: 'male', label: 'Masculino' },
  { value: 'non_binary', label: 'No binario' },
  { value: 'other', label: 'Otro' },
  { value: 'prefer_not_to_say', label: 'Prefiero no responder' },
];

const PRONOUN_OPTIONS: Array<{ value: RegistrationInput['pronoun']; label: string }> = [
  { value: 'he', label: 'Él' },
  { value: 'she', label: 'Ella' },
  { value: 'they', label: 'Elle' },
  { value: 'other', label: 'Otro' },
];

/** Pronombre que se ofrece según el género, mientras nadie haya elegido uno a mano. */
const SUGGESTED_PRONOUN: Partial<Record<RegistrationInput['gender'], RegistrationInput['pronoun']>> = {
  female: 'she',
  male: 'he',
  non_binary: 'they',
};

const DISABILITY_OPTIONS: Array<{ value: Disability; label: string }> = [
  { value: 'none', label: 'Ninguna' },
  { value: 'total_blindness', label: 'Ceguera total' },
  { value: 'low_vision', label: 'Baja visión' },
  { value: 'hearing', label: 'Auditiva' },
  { value: 'motor', label: 'Motriz' },
  { value: 'cognitive', label: 'Intelectual o cognitiva' },
  { value: 'communication', label: 'Comunicación o habla' },
  { value: 'autism_spectrum', label: 'TEA (Trastorno del Espectro Autista)' },
  { value: 'mental_health', label: 'Salud mental o psicosocial' },
  { value: 'other', label: 'Otra' },
  { value: 'prefer_not_to_say', label: 'Prefiero no responder' },
];

/** Estas dos vacían cualquier otra elección, y se vacían si se elige otra cosa. */
const EXCLUSIVE_DISABILITY: Disability[] = ['none', 'prefer_not_to_say'];

const MODALITY_OPTIONS: Array<{ value: RegistrationInput['modality']; label: string }> = [
  { value: 'in_person', label: 'Presencial' },
  { value: 'virtual', label: 'Virtual' },
  { value: 'both', label: 'Ambas' },
];

function ageAt(birthDate: Date, moment: Date): number {
  let age = moment.getFullYear() - birthDate.getFullYear();
  const beforeBirthday =
    moment.getMonth() < birthDate.getMonth() ||
    (moment.getMonth() === birthDate.getMonth() && moment.getDate() < birthDate.getDate());
  return beforeBirthday ? age - 1 : age;
}

/**
 * Formulario de registro: se completa una sola vez, después del primer login.
 *
 * Se muestra en lugar de toda la aplicación —sin cabecera ni menú— mientras
 * la cuenta no tenga fila de perfil. `App.tsx` decide cuándo mostrarlo; este
 * componente solo sabe pedir los datos y guardarlos.
 *
 * A diferencia del formulario de referencia en el sitio Jekyll, que es un
 * asistente de seis pasos con progreso guardado en `localStorage`, esta
 * primera versión pide todo en una sola pantalla: mismos campos, mismas
 * reglas, sin la mecánica de pasos. Se puede dividir en pasos más adelante
 * sin cambiar los datos que pide.
 */
export function RegistrationGate() {
  const { state, refresh } = useSession();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const [disability, setDisability] = useState<Disability[]>([]);
  const [pronounTouched, setPronounTouched] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const disabilityGroupId = useId();

  const email = state.status === 'authenticated' ? state.profile.user.email : '';
  const userId = state.status === 'authenticated' ? state.profile.user.id : '';

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      firstName: '',
      lastName: '',
      displayName: '',
      birthDate: '',
      country: '',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      gender: '',
      pronoun: '',
      accessibilityNotes: '',
      modality: '',
    },
  });

  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  const gender = watch('gender');
  useEffect(() => {
    if (pronounTouched || !gender) return;
    const suggestion = SUGGESTED_PRONOUN[gender];
    if (suggestion) setValue('pronoun', suggestion);
  }, [gender, pronounTouched, setValue]);

  const toggleDisability = (value: Disability) => {
    setDisability((current) => {
      const selected = current.includes(value);
      if (selected) return current.filter((item) => item !== value);
      if (EXCLUSIVE_DISABILITY.includes(value)) return [value];
      return [...current.filter((item) => !EXCLUSIVE_DISABILITY.includes(item)), value];
    });
  };

  const onSubmit = async (values: FormValues) => {
    if (!disability.length) return;
    setSubmitError('');
    setSubmitting(true);
    try {
      const via = await registeredVia();
      await createOwnProfile(userId, {
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        displayName: values.displayName.trim(),
        birthDate: new Date(`${values.birthDate}T00:00:00`),
        country: values.country,
        timezone: values.timezone,
        gender: values.gender as RegistrationInput['gender'],
        pronoun: values.pronoun as RegistrationInput['pronoun'],
        disability,
        accessibilityNotes: values.accessibilityNotes.trim(),
        modality: values.modality as RegistrationInput['modality'],
        registeredVia: via,
      });
      await refresh();
    } catch (error) {
      console.error('[Rol Sensorial] No se pudo crear el perfil.', error);
      reportClientError({
        source: 'web-app',
        operation: 'create-own-profile',
        message: 'No se pudo crear el perfil.',
        details: String(error),
      });
      setSubmitError('No pudimos guardar tu registro. Revisa tu conexión e inténtalo de nuevo.');
      setSubmitting(false);
    }
  };

  const timeZones = availableTimeZones();
  const today = new Date();
  const maxBirthDate = new Date(today.getFullYear() - 18, today.getMonth(), today.getDate())
    .toISOString()
    .slice(0, 10);

  return (
    <main id="main-content" className="container py-4">
      <section className="state-panel">
        <h1 ref={headingRef} tabIndex={-1}>Completa tu registro</h1>
        <p className="lead">
          Antes de entrar necesitamos algunos datos. Se guardan una sola vez y podrás revisarlos
          después desde tu perfil.
        </p>

        <form className="rs-form" noValidate onSubmit={handleSubmit(onSubmit)}>
          <fieldset>
            <legend>Datos personales</legend>

            <div className="mb-3">
              <label className="form-label" htmlFor="reg-first-name">Nombre</label>
              <input
                className="form-control"
                id="reg-first-name"
                type="text"
                autoComplete="given-name"
                aria-invalid={errors.firstName ? true : undefined}
                aria-describedby={errors.firstName ? 'reg-first-name-error' : undefined}
                {...register('firstName', { validate: (value) => value.trim().length > 0 || 'Escribe tu nombre.' })}
              />
              {errors.firstName ? <p id="reg-first-name-error" className="rs-error" role="alert">{errors.firstName.message}</p> : null}
            </div>

            <div className="mb-3">
              <label className="form-label" htmlFor="reg-last-name">Apellido</label>
              <input
                className="form-control"
                id="reg-last-name"
                type="text"
                autoComplete="family-name"
                aria-invalid={errors.lastName ? true : undefined}
                aria-describedby={errors.lastName ? 'reg-last-name-error' : undefined}
                {...register('lastName', { validate: (value) => value.trim().length > 0 || 'Escribe tu apellido.' })}
              />
              {errors.lastName ? <p id="reg-last-name-error" className="rs-error" role="alert">{errors.lastName.message}</p> : null}
            </div>

            <div className="mb-3">
              <label className="form-label" htmlFor="reg-display-name">
                Apodo <span className="visually-hidden">opcional</span>
              </label>
              <input
                className="form-control"
                id="reg-display-name"
                type="text"
                aria-describedby="reg-display-name-help"
                aria-invalid={errors.displayName ? true : undefined}
                {...register('displayName', {
                  validate: (value) => {
                    const length = value.trim().length;
                    if (!length) return true;
                    return (length >= 2 && length <= 48) || 'El apodo necesita entre 2 y 48 caracteres.';
                  },
                })}
              />
              <p id="reg-display-name-help" className="form-text">
                Es el nombre con el que te van a leer en el sitio. Se puede repetir con el de otras
                personas, y lo puedes cambiar cuando quieras desde tu perfil.
              </p>
              {errors.displayName ? <p className="rs-error" role="alert">{errors.displayName.message}</p> : null}
            </div>

            <div className="mb-3">
              <label className="form-label" htmlFor="reg-email">Correo electrónico</label>
              <input className="form-control" id="reg-email" type="email" value={email} readOnly disabled />
            </div>

            <div className="mb-3">
              <label className="form-label" htmlFor="reg-birth-date">Fecha de nacimiento</label>
              <input
                className="form-control"
                id="reg-birth-date"
                type="date"
                max={maxBirthDate}
                aria-describedby="reg-birth-date-help"
                aria-invalid={errors.birthDate ? true : undefined}
                {...register('birthDate', {
                  validate: (value) => {
                    if (!value) return 'Elige tu fecha de nacimiento.';
                    const parsed = new Date(`${value}T00:00:00`);
                    if (Number.isNaN(parsed.getTime())) return 'La fecha no es válida.';
                    return ageAt(parsed, today) >= 18 || 'Necesitas al menos 18 años para registrarte.';
                  },
                })}
              />
              <p id="reg-birth-date-help" className="form-text">Rol Sensorial es para personas mayores de 18 años.</p>
              {errors.birthDate ? <p className="rs-error" role="alert">{errors.birthDate.message}</p> : null}
            </div>
          </fieldset>

          <fieldset>
            <legend>Ubicación</legend>

            <div className="mb-3">
              <label className="form-label" htmlFor="reg-country">País</label>
              <select
                className="form-select"
                id="reg-country"
                aria-invalid={errors.country ? true : undefined}
                {...register('country', { validate: (value) => value.length > 0 || 'Elige tu país.' })}
              >
                <option value="">Selecciona tu país</option>
                {countries.map((country) => (
                  <option key={country.code} value={country.code}>{country.name}</option>
                ))}
              </select>
              {errors.country ? <p className="rs-error" role="alert">{errors.country.message}</p> : null}
            </div>

            <div className="mb-3">
              <label className="form-label" htmlFor="reg-timezone">Zona horaria</label>
              <select className="form-select" id="reg-timezone" {...register('timezone')}>
                {timeZones.map((zone) => (
                  <option key={zone} value={zone}>{`${zone} (${timeZoneOffsetLabel(zone)})`}</option>
                ))}
              </select>
            </div>
          </fieldset>

          <fieldset>
            <legend>Identidad</legend>

            <fieldset className="mb-3">
              <legend className="h6">Género</legend>
              {GENDER_OPTIONS.map((option) => (
                <div className="form-check" key={option.value}>
                  <input
                    className="form-check-input"
                    type="radio"
                    id={`reg-gender-${option.value}`}
                    value={option.value}
                    {...register('gender', { validate: (value) => value.length > 0 || 'Elige una opción.' })}
                  />
                  <label className="form-check-label" htmlFor={`reg-gender-${option.value}`}>{option.label}</label>
                </div>
              ))}
              {errors.gender ? <p className="rs-error" role="alert">{errors.gender.message}</p> : null}
            </fieldset>

            <fieldset className="mb-3">
              <legend className="h6">Pronombre</legend>
              {PRONOUN_OPTIONS.map((option) => (
                <div className="form-check" key={option.value}>
                  <input
                    className="form-check-input"
                    type="radio"
                    id={`reg-pronoun-${option.value}`}
                    value={option.value}
                    onClick={() => setPronounTouched(true)}
                    {...register('pronoun', { validate: (value) => value.length > 0 || 'Elige una opción.' })}
                  />
                  <label className="form-check-label" htmlFor={`reg-pronoun-${option.value}`}>{option.label}</label>
                </div>
              ))}
              {errors.pronoun ? <p className="rs-error" role="alert">{errors.pronoun.message}</p> : null}
            </fieldset>
          </fieldset>

          <fieldset>
            <legend>Accesibilidad</legend>
            <p>
              Estos datos son privados: los usamos para adaptar tus partidas y nunca se comparten
              automáticamente sin tu consentimiento.
            </p>

            <fieldset className="mb-3" aria-describedby={disability.length ? undefined : `${disabilityGroupId}-error`}>
              <legend className="h6">¿Tienes alguna discapacidad?</legend>
              {DISABILITY_OPTIONS.map((option) => (
                <div className="form-check" key={option.value}>
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id={`reg-disability-${option.value}`}
                    checked={disability.includes(option.value)}
                    onChange={() => toggleDisability(option.value)}
                  />
                  <label className="form-check-label" htmlFor={`reg-disability-${option.value}`}>{option.label}</label>
                </div>
              ))}
              {!disability.length ? (
                <p id={`${disabilityGroupId}-error`} className="rs-error" role="alert">Elige al menos una opción.</p>
              ) : null}
            </fieldset>

            <div className="mb-3">
              <label className="form-label" htmlFor="reg-accessibility-notes">
                Notas de accesibilidad <span className="visually-hidden">opcional</span>
              </label>
              <textarea
                className="form-control"
                id="reg-accessibility-notes"
                rows={3}
                {...register('accessibilityNotes')}
              />
            </div>
          </fieldset>

          <fieldset>
            <legend>Preferencias de juego</legend>
            <fieldset className="mb-3">
              <legend className="h6">Modalidad</legend>
              {MODALITY_OPTIONS.map((option) => (
                <div className="form-check" key={option.value}>
                  <input
                    className="form-check-input"
                    type="radio"
                    id={`reg-modality-${option.value}`}
                    value={option.value}
                    {...register('modality', { validate: (value) => value.length > 0 || 'Elige una opción.' })}
                  />
                  <label className="form-check-label" htmlFor={`reg-modality-${option.value}`}>{option.label}</label>
                </div>
              ))}
              {errors.modality ? <p className="rs-error" role="alert">{errors.modality.message}</p> : null}
            </fieldset>
          </fieldset>

          <button className="btn btn-accent" type="submit" disabled={submitting}>
            {submitting ? 'Guardando…' : 'Confirmar registro'}
          </button>

          {submitError ? <p className="rs-error" role="alert">{submitError}</p> : null}
        </form>
      </section>
    </main>
  );
}
